import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import CircularProgress from '@mui/material/CircularProgress';
import Backdrop from '@mui/material/Backdrop';
import LinearProgress from '@mui/material/LinearProgress';
import Autocomplete from "@mui/material/Autocomplete";
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import FormHelperText from '@mui/material/FormHelperText';
import FormGroup from "@mui/material/FormGroup";
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';

import { useForm } from '../../General/useForm';
import Loader from '../../General/Loader';
import { fetchCountry, fetchState } from '../../General/countryStateCitySlice';

import { fetchProfile, updateProductSettings, resetProfile } from '../Profile/profileSlice';
import Notification from '../../../pages/General/Notification';

// Global enterprise styling dictionary configuring standardized form element matrices
const enterpriseProductInputStyles = {
    width: '100%',
    '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        backgroundColor: '#FFFFFF',
        transition: 'all 0.2s ease-in-out',
        border: '1px solid #E2E8F0',
        '& fieldset': { border: 'none' },
        '&:hover': {
            borderColor: '#CBD5E1',
            boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
        },
        '&.Mui-focused': {
            borderColor: '#1976d2', // --- CORPORATE BLUE BRAND KEY TOKEN ---
            boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.08)'
        }
    },
    '& .MuiInputLabel-outlined': {
        color: '#64748B',
        fontWeight: 500,
        transform: 'translate(14px, 10px) scale(1)',
        '&.Mui-focused, &.MuiInputLabel-shrink': {
            transform: 'translate(14px, -8px) scale(0.75)',
            color: '#1976d2',
            fontWeight: 600
        }
    },
    '& input, & .MuiSelect-select': {
        padding: '10px 14px !important',
        fontSize: '13.5px',
        color: '#1E293B',
        fontWeight: 500
    },
    '& .MuiFormHelperText-root': {
        mx: 0.5,
        mt: 0.75,
        fontWeight: 500
    }
};

const ProductSettingsPage = () => {
    const dispatch = useDispatch();
    const profile = useSelector(state => state.profile);
    const country = useSelector(state => state.countrystate.country);
  
    const initialValues = {
        country_of_origin: '',
        fda: '',
    }
    const [values, setValues] = useState(initialValues);

    const [errors, setErrors] = useState(initialValues);
    const filteredCountryData = country.data.filter(({ code }) => code );

    const onSelectCountryChange = (countryCode) => {
        setValues({
          ...values,
          country_of_origin: countryCode,
        });
        setErrors({ country_of_origin: "" });
      };

    const getSelectedCountry = (country_code) => {
        let filteredValue = { code: "", name: "" };
        filteredCountryData.map((key) => {
          if (key.code == country_code) {
            filteredValue = key;
          }
        });
         return filteredValue;
      };

    const handleChange = (event, type) => {
        setValues({
          ...values,
          fda: event.target.value,
        });
        setErrors({ fda: "" });
    };

    const handleSubmit = e => {
        e.preventDefault()
        if (typeof values.country_of_origin ==="undefined") {
            setErrors({
                ...errors,
                country_of_origin: "This field is required"
            })
        } else if (typeof values.fda ==="undefined") {
            setErrors({
                ...errors,
                fda: "This field is required"
            })
        }else{
            dispatch(updateProductSettings(profile && profile.profile && profile.profile.id,values))
            dispatch(fetchProfile())
        }
    }
     
    useEffect(() => {
        dispatch(fetchCountry());
    }, [dispatch]);

    useEffect(() => {
        dispatch(fetchProfile())
    }, [dispatch])

    useEffect(() => {
        if (profile) {
            setValues({
                ...values,
                country_of_origin:profile && profile.profile  && profile.profile.country_of_origin,
                fda: profile && profile.profile  && profile.profile.fda,
            })
        }
    }, [profile]);

    return (
        <Box className="sc--settings--card" sx={{ width: '100%', boxSizing: 'border-box', py: 1 }}>
             {(profile && profile.notification && profile.notification.show) && (
                <Notification
                    is_open={profile.notification.show}
                    messageType={profile.notification.messageType}
                    messages={profile.notification.messageContent}
                    handleClose={() => dispatch(resetProfile())}
                />
            )}
            
            <Card 
                className="sc--cardhead--details"
                sx={{ 
                    width: '100%',
                    maxWidth: 540,
                    mx: 'auto',
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E2E8F0',
                    borderRadius: '16px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.02)',
                    overflow: 'hidden'
                }}
            >
                <CardHeader 
                    title={
                        <Typography variant="h6" component="h1" sx={{ fontWeight: 800, color: '#0F172A', fontSize: '16px', m: 0 }}>
                            Product Settings
                        </Typography>
                    } 
                    sx={{ p: 2.5, backgroundColor: '#FFFFFF' }}
                />
                
                {profile && profile.loading === true && (
                    <LinearProgress sx={{ height: '3px', backgroundColor: '#E2E8F0', '& .MuiLinearProgress-bar': { backgroundColor: '#1976d2' } }} />
                )}
                
                <Divider sx={{ borderColor: '#E2E8F0' }} />
                
                <CardContent sx={{ p: 4, boxSizing: 'border-box' }} component="form" onSubmit={handleSubmit} noValidate>
                    <Stack spacing={3.5}>
                        
                        {/* 1. Country Of Origin Dynamic Autocomplete Matrix Selector field */}
                        <Box className="sc--form--center" sx={{ width: '100%', minHeight: '44px' }}>
                            <Autocomplete
                                className="help-text-error"
                                disablePortal
                                size="small"
                                name="country_of_origin"
                                id="country-select-demo"
                                options={filteredCountryData}
                                autoHighlight
                                loading={country && country.loading}
                                getOptionLabel={(option) => option.name}
                                value={getSelectedCountry(values && values.country_of_origin)}
                                sx={{
                                    width: '100%',
                                    '& .MuiAutocomplete-inputRoot': enterpriseProductInputStyles['& .MuiOutlinedInput-root'],
                                    '& .MuiInputLabel-root': enterpriseProductInputStyles['& .MuiInputLabel-outlined'],
                                    '& input': enterpriseProductInputStyles['& input']
                                }}
                                renderOption={(props, option) => (
                                    <MenuItem { ...props } value={option.code} key={option.code} sx={{ fontSize: '13.5px', fontWeight: 500, color: '#1E293B' }}>
                                        {option.icon} {option.name}
                                    </MenuItem>
                                )}
                                onChange={(event, value) => {
                                    if (value !== null) {
                                        onSelectCountryChange(value.code);
                                        setErrors({ country_of_origin: "" });
                                    } else {
                                        onSelectCountryChange("");
                                        setErrors({
                                            country_of_origin: "This field is required",
                                        });
                                    }
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        { ...params }
                                        label="Country of Origin"
                                        variant="outlined"
                                        error={!!errors.country_of_origin}
                                        helperText={errors.country_of_origin}
                                        sx={{
                                            '& .MuiFormHelperText-root': enterpriseProductInputStyles['& .MuiFormHelperText-root']
                                        }}
                                    />
                                )}
                            />
                        </Box>

                        {/* 2. FDA Regulatory Affirmation Interface Controller Panel */}
                        <Box className="sc--form--center" sx={{ width: '100%', display: 'flex', justifyContent: 'flex-start', pl: 0.5 }}>
                            <FormControl error={!!errors.fda} component="fieldset">
                                <FormLabel 
                                    id="fda-radio-buttons-group-label"
                                    sx={{ 
                                        fontSize: '12.5px', 
                                        fontWeight: 700, 
                                        color: '#64748B', 
                                        textTransform: 'uppercase', 
                                        letterSpacing: '0.5px',
                                        mb: 1.5,
                                        '&.Mui-focused': { color: '#64748B' }
                                    }}
                                >
                                    FDA Certification
                                </FormLabel>
                                <RadioGroup
                                    aria-labelledby="fda-radio-buttons-group-label"
                                    name="radio-buttons-group"
                                    value={values.fda || ""}
                                    onChange={event => handleChange(event)}
                                    row
                                    sx={{ gap: 3 }}
                                >
                                    <FormControlLabel 
                                        value="Y" 
                                        control={<Radio size="small" sx={{ color: '#CBD5E1', '&.Mui-checked': { color: '#1976d2' } }} />} 
                                        label={<Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#334155' }}>YES</Typography>} 
                                    />
                                    <FormControlLabel 
                                        value="N" 
                                        control={<Radio size="small" sx={{ color: '#CBD5E1', '&.Mui-checked': { color: '#1976d2' } }} />} 
                                        label={<Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#334155' }}>NO</Typography>} 
                                    />
                                </RadioGroup>
                                {errors.fda && (
                                    <FormHelperText sx={{ color: "#EF4444", mx: 0, mt: 1, fontWeight: 500 }}>
                                        {errors.fda}
                                    </FormHelperText>
                                )}
                            </FormControl>
                        </Box>

                        {/* 3. Operational Submit Terminal Actions Control Trigger Footer */}
                        <Box className="sc--form--center" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', pt: 1, borderTop: '1px solid #E2E8F0' }}>
                            <Button 
                                variant="contained" 
                                type="submit"
                                sx={{
                                    textTransform: 'none',
                                    borderRadius: '10px',
                                    fontWeight: 700,
                                    fontSize: '13.5px',
                                    height: '38px',
                                    px: 4,
                                    boxShadow: 'none',
                                    backgroundColor: '#1976d2',
                                    color: '#FFFFFF',
                                    '&:hover': { backgroundColor: '#115293', boxShadow: 'none' }
                                }}
                            >
                                Save Settings
                            </Button>
                        </Box>
                        
                    </Stack>
                </CardContent>
            </Card>
        </Box>
    )
}

export default ProductSettingsPage;